<script>
import baseMixin from '@/mixins/baseMixin'
import dayjs from '@/services/date/index'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'

export default {
  name: 'DatePicker',
  mixins: [baseMixin],
  props: {
    value: { type: [Array, String, Date], default: () => [] }, // [startDate, endDate]
    startDate: { type: [String, Date], default: null },
    endDate: { type: [String, Date], default: null },
    calendarType: { type: String, default: '' },
    condition: { type: String, default: 'is' },
  },
  emits: [
    'input',
    'update-condition',
    'confirm-date-filter',
    'clear-fields',
    'close-date-filter',
  ],
  data() {
    const today = new Date()
    let initialDate = today
    if (this.calendarType === 'range') {
      if (Array.isArray(this.value) && this.value[0]) {
        initialDate = new Date(this.value[0])
      }
    } else if (this.startDate) {
      initialDate = new Date(this.startDate)
    }

    return {
      startLabel: 'Start date',
      localCondition: this.condition,
      conditions: ['is', 'is before', 'is after', 'is between'],
      activePicker: 'start',
      today,
      currentMonth: initialDate.getMonth(),
      currentYear: initialDate.getFullYear(),
      selectedDate: new Date(today),
      localStart: this.startDate,
      localEnd: this.endDate,
      view: 'calendar',
      days: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      monthNames: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
    }
  },
  computed: {
    years() {
      return Array.from({ length: 100 }, (_, i) => 1990 + i)
    },
    calendarDays() {
      const days = []
      const firstDay = new Date(this.currentYear, this.currentMonth, 1)
      const startDay = firstDay.getDay()
      const daysInMonth = new Date(
        this.currentYear,
        this.currentMonth + 1,
        0
      ).getDate()
      const prevMonthDays = new Date(
        this.currentYear,
        this.currentMonth,
        0
      ).getDate()

      // Previous month padding
      for (let i = startDay - 1; i >= 0; i--) {
        days.push(
          new Date(this.currentYear, this.currentMonth - 1, prevMonthDays - i)
        )
      }

      // Current month
      for (let i = 1; i <= daysInMonth; i++) {
        days.push(new Date(this.currentYear, this.currentMonth, i))
      }

      // Next month padding
      while (days.length % 7 !== 0) {
        days.push(
          new Date(this.currentYear, this.currentMonth + 1, days.length % 7)
        )
      }

      return days
    },
    activePickerValue: {
      get() {
        return this.activePicker === 'start' ? this.localStart : this.localEnd
      },
      set(val) {
        if (this.activePicker === 'start') {
          this.localStart = val
        } else {
          this.localEnd = val
        }

        if (
          this.localCondition === 'is between' &&
          this.activePicker === 'start'
        ) {
          this.activePicker = 'end'
        }
      },
    },
    minDate() {
      return this.localCondition === 'is after' || this.activePicker === 'end'
        ? this.startDate
        : null
    },
    maxDate() {
      return this.localCondition === 'is before' ? this.localStart : null
    },
    displayedStartDate() {
      return this.calendarType !== 'range'
        ? this.localStart
          ? this.formatCalenderDate(this.localStart)
          : ''
        : this.value[0]
        ? this.formatCalenderDate(this.value[0])
        : ''
    },
    displayedEndDate() {
      return this.calendarType !== 'range'
        ? this.localCondition === 'is between' && this.localEnd
          ? this.formatCalenderDate(this.localEnd)
          : ''
        : this.localCondition === 'is between' && this.value[1]
        ? this.formatCalenderDate(this.value[1])
        : ''
    },
  },
  watch: {
    startDate(newVal) {
      this.localStart = newVal ? new Date(newVal) : null
    },
    endDate(newVal) {
      this.localEnd = newVal ? new Date(newVal) : null
    },
    condition(newVal) {
      this.localCondition = newVal
    },
    value: {
      immediate: true,
      handler(newVal) {
        if (this.calendarType === 'range') {
          if (Array.isArray(newVal)) {
            this.localStart = newVal[0] ? new Date(newVal[0]) : null
            this.localEnd = newVal[1] ? new Date(newVal[1]) : null
          }
        }
      },
    },
  },
  methods: {
    onConditionChange(val) {
      this.localCondition = val
      this.$emit('update-condition', val) // custom event
    },
    formatCalenderDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11
        this.currentYear--
      } else {
        this.currentMonth--
      }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0
        this.currentYear++
      } else {
        this.currentMonth++
      }
    },
    selectMonth(month) {
      this.currentMonth = month
      this.view = 'calendar'
    },
    selectYear(year) {
      this.currentYear = year
      this.view = 'calendar'
    },
    selectDate(date) {
      this.selectedDate = new Date(date)
      this.currentMonth = date.getMonth()
      this.currentYear = date.getFullYear()
      this.onDatePick(this.selectedDate)
    },
    isSameDate(d1, d2) {
      return (
        d1?.getDate() === d2?.getDate() &&
        d1?.getMonth() === d2?.getMonth() &&
        d1?.getFullYear() === d2?.getFullYear()
      )
    },
    isBetween(date) {
      return (
        this.localStart &&
        this.localEnd &&
        date > this.localStart &&
        date < this.localEnd
      )
    },
    getDayClassRange(date) {
      const isStart = this.isSameDate(date, this.localStart)
      const isEnd = this.isSameDate(date, this.localEnd)
      const isSingle = isStart && isEnd

      return {
        today: this.isSameDate(date, this.today),
        start: isStart,
        end: isEnd,
        between: this.isBetween(date),
        'other-month': date.getMonth() !== this.currentMonth,
        'start-end': isSingle, // optional class if you want special handling
      }
    },
    // common
    clearFields() {
      if (this.calendarType !== 'range') {
        this.localStart = null
        this.localEnd = null
        this.activePicker = 'start'
      }
      this.$emit('clear-fields')
    },
    // for normal calendar
    isSelected(date) {
      return (
        this.selectedDate.getDate() === date.getDate() &&
        this.selectedDate.getMonth() === date.getMonth() &&
        this.selectedDate.getFullYear() === date.getFullYear()
      )
    },
    getDayClass(date) {
      return {
        today: this.isSameDate(date, this.today),
        selected: this.isSelected(date),
        'other-month': date.getMonth() !== this.currentMonth,
      }
    },
    confirmDateFilter() {
      const format = (d) =>
        d ? dayjs(d).format(DateFormatToken.YYYYMMDD) : null

      let start = null
      let end = null

      switch (this.localCondition) {
        case 'is':
          start = format(this.localStart)
          end = format(this.localStart)
          break
        case 'is before':
          start = null
          end = format(this.localStart)
          break
        case 'is after':
          start = format(this.localStart)
          end = null
          break
        case 'is between':
          start = format(this.localStart)
          end = format(this.localEnd)
          break
      }

      if (!this.localStart && !this.localEnd) {
        start = dayjs().format('YYYY-MM-DD')
        end = null
      }
      this.$emit('confirm-date-filter', { startDate: start, endDate: end })
    },
    onDatePick(val) {
      if (this.localCondition === 'is between') {
        if (this.activePicker === 'start') {
          this.localStart = val
          this.activePicker = 'end'
          this.localEnd = null
        } else if (this.activePicker === 'end') {
          if (this.localStart && dayjs(val).isBefore(dayjs(this.localStart))) {
            this.localStart = val
            this.localEnd = null

            this.activePicker = 'start'
            this.$nextTick(() => {
              this.activePicker = 'end'
            })
          } else {
            this.localEnd = val
          }
        }
      } else {
        this.localStart = val
        this.localEnd = null
      }
      this.$emit('input', [this.localStart, this.localEnd])
    },
  },
}
</script>

<template>
  <div
    class="custom-datepicker-popup"
    :class="{ 'date-filter-container': calendarType !== 'range' }"
  >
    <div class="date-selection-header">
      <div class="d-flex" style="justify-content: flex-start; width: 100%">
        <v-select
          v-model="startLabel"
          :items="['Start date']"
          style="margin-right: 10px"
          outlined
        />
        <v-select
          :id="'conditions'"
          :items="conditions"
          :value="localCondition"
          no-border
          outlined
          menu-props="{ contentClass: 'condition-dropdown' }"
          @input="onConditionChange"
        />
      </div>
      <div
        class="d-flex"
        style="justify-content: space-between; margin-bottom: 15px"
      >
        <div
          class="date-show-container"
          :class="{
            active: activePicker === 'start',
            'date-show-container-clickable': localCondition === 'is between',
          }"
          @click="localCondition === 'is between' && (activePicker = 'start')"
        >
          {{ displayedStartDate }}
        </div>
        <div
          class="date-show-container"
          :class="{
            disabled: localCondition !== 'is between',
            active: activePicker === 'end' && localCondition === 'is between',
            'date-show-container-clickable': localCondition === 'is between',
          }"
        >
          {{ displayedEndDate }}
        </div>
      </div>
    </div>

    <v-divider style="margin-bottom: 10px"></v-divider>

    <div class="datepicker">
      <!-- Header -->
      <div class="datepicker-header">
        <div class="selectors">
          <span @click="view = 'month'">{{ monthNames[currentMonth] }}</span>
          <span @click="view = 'year'">{{ currentYear }}</span>
          <v-icon size="20" class="mb-1" @click="view = 'year'">mdi-menu-down</v-icon>
        </div>
        <div class="nav-buttons">
          <BaseButton
            xs
            width="10"
            color="white"
            text-color="grey"
            hover-color="transparent"
            @click="prevMonth"
          >
            <v-icon size="20">mdi-chevron-left</v-icon>
          </BaseButton>
          <BaseButton
            xs
            width="10"
            color="white"
            text-color="grey"
            hover-color="transparent"
            @click="nextMonth"
          >
            <v-icon size="20">mdi-chevron-right</v-icon>
          </BaseButton>
        </div>
      </div>

      <!-- Calendar View -->
      <div
        v-if="view === 'calendar'"
        :class="{ calendar: calendarType !== 'range' }"
      >
        <div class="day-names">
          <div v-for="day in days" :key="`calendar-day-name-${day}`">{{
            day
          }}</div>
        </div>
        <div
          class="days"
          :class="{
            'has-range': calendarType === 'range' && localStart && localEnd,
          }"
        >
          <div
            v-for="(date, index) in calendarDays"
            :key="`calendar-day-${index}`"
            :class="[
              calendarType === 'range' ? 'day' : 'day-of-date',
              calendarType === 'range'
                ? getDayClassRange(date)
                : getDayClass(date),
            ]"
            @click="selectDate(date)"
          >
            {{ date.getDate() }}
          </div>
        </div>
      </div>

      <!-- Month View -->
      <div v-if="view === 'month'" class="month-grid">
        <div
          v-for="(month, index) in monthNames"
          :key="`calendar-month-${index}`"
          :class="{ selected: index === currentMonth }"
          @click="selectMonth(index)"
        >
          {{ month }}
        </div>
      </div>

      <!-- Year View -->
      <div v-if="view === 'year'" class="year-scroll">
        <div
          v-for="year in years"
          :key="`calendar-year-${year}`"
          :class="{ selected: year === currentYear }"
          @click="selectYear(year)"
        >
          {{ year }}
        </div>
      </div>
    </div>

    <v-divider></v-divider>

    <v-card-actions class="justify-space-between px-4">
      <BaseButton
        text
        xs
        width="10"
        class="date-picker-btns"
        @click="clearFields"
      >
        {{ $t('general.clear') }}
      </BaseButton>
      <v-spacer></v-spacer>
      <BaseButton
        text
        xs
        width="10"
        class="date-picker-btns"
        @click="$emit('close-date-filter')"
      >
        {{ $t('general.cancel') }}
      </BaseButton>
      <BaseButton
        text
        xs
        width="10"
        :class="['date-picker-ok-btn', colorClass.text.blue400]"
        @click="
          () => {
            calendarType !== 'range'
              ? confirmDateFilter()
              : $emit('confirm-date-filter')
          }
        "
      >
        {{ $t('exportBookings.buttons.ok') }}
      </BaseButton>
    </v-card-actions>
  </div>
</template>

<style lang="scss">
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/date-picker.scss';
</style>
